import React, {useMemo} from 'react';
import {Typography, Box} from '@mui/material';
import ArrRadio from '../../../common/arr-radio/ArrRadio';
import ArrCheckboxList from '../../../common/arr-checkbox-list/ArrCheckboxList';
import './WidgetsCustomizationReviewerSection.scss';
import {t} from 'i18next';

type Reviewer = {
  reviewerID: number;
  reviewerName: string;
};

type ReviewerOption = 'todoDocs' | 'completeDocs' | 'specificReviewers';

type ReviewerLabels = Partial<{
  toDo: string;
  completeDocs: string;
}>;

type WidgetsCustomizationReviewerSectionProps = {
  title?: string;
  reviewerOption: ReviewerOption;
  onReviewerOptionChange: (value: ReviewerOption) => void;
  reviewers: Reviewer[];
  selectedReviewers: string[];
  onToggleReviewer: (reviewerName: string) => void;
  labels?: ReviewerLabels;
};

export const WidgetsCustomizationReviewerSection: React.FC<
  WidgetsCustomizationReviewerSectionProps
> = ({
  title = 'Select Reviewers',
  reviewerOption,
  onReviewerOptionChange,
  reviewers,
  selectedReviewers,
  onToggleReviewer,
  labels,
}) => {
  const toDoLabel =
    labels?.toDo ?? t('dashboard.widgetCustomization.reviewerSection.toDo');
  const completeDocsLabel =
    labels?.completeDocs ??
    t('dashboard.widgetCustomization.reviewerSection.completeDocs');
  const specificLabel = t(
    'dashboard.widgetCustomization.reviewerSection.specific'
  );
  const radioOptions = useMemo(
    () => [
      {label: toDoLabel, value: 'todoDocs'},
      {label: completeDocsLabel, value: 'completeDocs'},
      {label: specificLabel, value: 'specificReviewers'},
    ],
    [toDoLabel, completeDocsLabel, specificLabel]
  );

  const childLabels = useMemo(
    () =>
      reviewers.map((r) => {
        const fullName = `${r.reviewerName}`;
        return {
          label: fullName,
          value: selectedReviewers.includes(fullName),
          disabled:
            selectedReviewers.length == 6 &&
            !selectedReviewers.includes(fullName),
        };
      }),
    [reviewers, selectedReviewers]
  );

  const checkboxListKey = useMemo(
    () =>
      reviewers
        .map((r, idx) => {
          const fullName = `${r.reviewerName}`;
          const isSel = selectedReviewers.includes(fullName) ? '1' : '0';
          return `${idx}:${fullName}:${isSel}`;
        })
        .join('|'),
    [reviewers, selectedReviewers]
  );

  const handleCheckboxListChange = (newChecked: boolean[]) => {
    const currentChecked = childLabels.map((c) => c.value);

    newChecked.forEach((isChecked, idx) => {
      if (isChecked !== currentChecked[idx]) {
        const fullName = `${reviewers[idx].reviewerName}`;
        onToggleReviewer(fullName);
      }
    });
  };

  return (
    <Box className="reviewer-section">
      <Typography
        className="typo-text subhead4_size u-mb-1"
        data-testid={`widget-customization-title`}
      >
        {title}
      </Typography>
      <div
        className="reviewers-radio-group"
        data-testid="reviewers-radio-group"
      >
        <ArrRadio
          name="reviewers-option"
          value={reviewerOption}
          onChange={(val: string) =>
            onReviewerOptionChange(val as ReviewerOption)
          }
          data={radioOptions}
          className="reviewers-radio"
          selected={radioOptions.some((opt) => opt.value === reviewerOption)}
        />
      </div>
      {reviewerOption === 'specificReviewers' && (
        <div
          className="reviewers-selection"
          data-testid={`widget-customization-specific-group`}
        >
          <ArrCheckboxList
            key={checkboxListKey}
            parentLabel=""
            childLabels={childLabels}
            onChange={handleCheckboxListChange}
            size="medium"
          />

          <div
            style={{display: 'none'}}
            data-testid={`widgets-customization-reviewer-section-div`}>
            {reviewers.map((reviewer, index) => (
              <span
                key={index}
                data-testid={`widget-customization-reviewer-${reviewer.reviewerID}`}
              />
            ))}
          </div>
        </div>
      )}
    </Box>
  );
};
